// src/pages/admin/UserBalance.tsx
import React, { useState, useEffect, Fragment } from 'react';
import { formatPriceSimple, SupportedCurrency } from '../../utils/currency';
import BalanceService, { Transaction, WithdrawalRequest } from '../../services/balance.service';
import BankService, { Bank } from '../../services/bank.service';
import AdminLayout from '../../components/layouts/AdminLayout';
import Card from '../../components/common/Card';
import Alert from '../../components/common/Alert';
import Loader from '../../components/common/Loader';
import Button from '../../components/common/Button';
import { Dialog, Transition } from '@headlessui/react';
import { 
  DollarSign, 
  Download, 
  CreditCard, 
  X, 
  AlertCircle, 
  CheckCircle, 
  Search, 
  RefreshCw, 
  Info,
  Check
} from 'react-feather';
import api from '../../services/api';

const UserBalance: React.FC = () => {
  const [balance, setBalance] = useState<number>(0);
  const [currency, setCurrency] = useState<SupportedCurrency>('NGN');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [withdrawals, setWithdrawals] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Withdrawal modal state
  const [isWithdrawalModalOpen, setIsWithdrawalModalOpen] = useState<boolean>(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState<string>('');
  const [withdrawalLoading, setWithdrawalLoading] = useState<boolean>(false);
  const [withdrawalError, setWithdrawalError] = useState<string | null>(null);
  const [withdrawalSuccess, setWithdrawalSuccess] = useState<boolean>(false);
  const [withdrawalMessage, setWithdrawalMessage] = useState<string>('');
  
  // Details modal state
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [refreshingStatus, setRefreshingStatus] = useState<string | null>(null);
  
  // Bank selection state
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loadingBanks, setLoadingBanks] = useState<boolean>(false);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [bankSearchQuery, setBankSearchQuery] = useState<string>('');
  const [accountName, setAccountName] = useState<string>('');
  const [accountNumber, setAccountNumber] = useState<string>('');
  const [verifiedAccount, setVerifiedAccount] = useState<boolean>(false);
  const [verifyingAccount, setVerifyingAccount] = useState<boolean>(false);
  const [userPremiumInfo, setUserPremiumInfo] = useState<{isPremium: boolean, premiumPlan?: string}>({
    isPremium: false
  });

const [pollingActive, setPollingActive] = useState<boolean>(false);
const [pollingTransactionId, setPollingTransactionId] = useState<string | null>(null);
const [pollingCount, setPollingCount] = useState<number>(0);

useEffect(() => {
  let pollingInterval: NodeJS.Timeout | null = null;
  
  if (pollingActive && pollingTransactionId) {
    // Set up polling every 3 seconds, up to 10 times (30 seconds total)
    pollingInterval = setInterval(() => {
      if (pollingCount < 10) {
        checkTransactionStatus(pollingTransactionId);
        setPollingCount(prev => prev + 1);
      } else {
        // Stop polling after 10 attempts
        stopPolling();
      }
    }, 3000);
  }
  
  return () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
  };
}, [pollingActive, pollingTransactionId, pollingCount]);

// Add these helper functions for polling
const startPolling = (transactionId: string) => {
  setPollingActive(true);
  setPollingTransactionId(transactionId);
  setPollingCount(0);
  console.log(`Started polling for transaction ${transactionId}`);
};

const stopPolling = () => {
  setPollingActive(false);
  setPollingTransactionId(null);
  setPollingCount(0);
  console.log('Stopped polling for transaction status');
};

// Replace your refreshTransactionStatus function with this updated version
const refreshTransactionStatus = async (reference: string) => {
  if (!reference) return;
  
  setRefreshingStatus(reference);
  try {
    const result = await BalanceService.checkWithdrawalStatus(reference);
    console.log('Status update result:', result);
    
    // Refresh data to show updated status
    await fetchBalance();
    
    // Show a notification based on the result
    if (result.status) {
      let message = `Status updated: ${result.message}`;
      
      if (result.data?.status === 'completed') {
        message = 'Your withdrawal has been completed successfully!';
      } else if (result.data?.status === 'failed') {
        message = `Withdrawal failed: ${result.data.failureReason || 'Unknown error'}`;
      }
        
      setError(message);
      setTimeout(() => setError(null), 3000);
    }
  } catch (err) {
    console.error('Error refreshing transaction status:', err);
    setError('Failed to refresh status. Please try again.');
  } finally {
    setRefreshingStatus(null);
  }
};

// Add this new function for checking transaction status during polling
const checkTransactionStatus = async (reference: string) => {
  if (!reference) return;
  
  try {
    console.log(`Checking status for transaction ${reference}`);
    const result = await BalanceService.checkWithdrawalStatus(reference);
    
    // If this is the first poll, check if the transaction is already completed or failed
    if (pollingCount === 0) {
      // Look through all transactions to find this one
      const existingTransaction = withdrawals.find(tx => tx.reference === reference);
      
      // If the transaction already has a final status, don't start polling
      if (existingTransaction && 
          (existingTransaction.status === 'completed' || existingTransaction.status === 'failed')) {
        console.log(`Transaction ${reference} already has final status: ${existingTransaction.status}`);
        stopPolling();
        return;
      }
    }
    
    // If we get a final status from the API, stop polling
    if (result.data?.status === 'completed' || result.data?.status === 'failed') {
      console.log(`Transaction ${reference} has final status: ${result.data.status}`);
      stopPolling();
      
      // Refresh balance to reflect the updated status
      await fetchBalance();
      
      // Show notification based on status
      if (result.data.status === 'completed') {
        setError('Your withdrawal has been completed successfully!');
        setTimeout(() => setError(null), 5000);
      } else if (result.data.status === 'failed') {
        setError(`Withdrawal failed: ${result.data.failureReason || 'Unknown error'}`);
        setTimeout(() => setError(null), 5000);
      }
    }
  } catch (err) {
    console.error('Error checking transaction status:', err);
  }
};

// Add a function to check if polling is needed for a specific transaction
const shouldPollTransaction = (transaction: Transaction): boolean => {
  // Only poll pending withdrawals
  return transaction.type === 'withdrawal' && transaction.status === 'pending';
};

// Add this to your fetchBalance function to automatically start polling for pending withdrawals
const fetchBalance = async () => {
  setLoading(true);
  try {
    const data = await BalanceService.getUserBalance();
    setBalance(data.accountBalance);
    setCurrency(data.currency as SupportedCurrency);
    setTransactions(data.transactions);
    
    // Filter for withdrawal transactions
    const withdrawalTxs = data.transactions.filter(tx => tx.type === 'withdrawal');
    setWithdrawals(withdrawalTxs);
    
    // If we're not already polling, check if we have any pending withdrawals to poll
    if (!pollingActive) {
      const pendingWithdrawal = withdrawalTxs.find(shouldPollTransaction);
      if (pendingWithdrawal && pendingWithdrawal.reference) {
        console.log(`Found pending withdrawal ${pendingWithdrawal.reference}, starting automatic polling`);
        startPolling(pendingWithdrawal.reference);
      }
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to load account balance';
    setError(errorMessage);
  } finally {
    setLoading(false);
  }
};

  
  useEffect(() => {
    fetchBalance();
    fetchUserInfo();
  }, []);

  // Fetch banks when withdrawal modal opens
  useEffect(() => {
    if (isWithdrawalModalOpen && banks.length === 0) {
      fetchBanks();
    }
  }, [isWithdrawalModalOpen]);

  // Verify account when bank and account number are set
  useEffect(() => {
    if (selectedBank && accountNumber && accountNumber.length === 10) {
      verifyAccountNumber();
    } else {
      setVerifiedAccount(false);
      setAccountName('');
    }
  }, [selectedBank, accountNumber]);

  const fetchUserInfo = async () => {
    try {
      const response = await api.get('/users/me');
      setUserPremiumInfo({
        isPremium: response.data.isPremium || false,
        premiumPlan: response.data.premiumPlan || 'Basic'
      });
    } catch (err) {
      console.error('Error fetching user info:', err);
    }
  };

  // const fetchBalance = async () => {
  //   setLoading(true);
  //   try {
  //     const data = await BalanceService.getUserBalance();
  //     setBalance(data.accountBalance);
  //     setCurrency(data.currency as SupportedCurrency);
  //     setTransactions(data.transactions);
      
  //     // Filter for withdrawal transactions
  //     const withdrawalTxs = data.transactions.filter(tx => tx.type === 'withdrawal');
  //     setWithdrawals(withdrawalTxs);
  //   } catch (err) {
  //     const errorMessage = err instanceof Error ? err.message : 'Failed to load account balance';
  //     setError(errorMessage);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchBanks = async () => {
    setLoadingBanks(true);
    try {
      const banksList = await BankService.getBanks();
      setBanks(banksList);
    } catch (err) {
      console.error('Error fetching banks:', err);
    } finally {
      setLoadingBanks(false);
    }
  };

  const verifyAccountNumber = async () => {
    if (!selectedBank || !accountNumber || accountNumber.length !== 10) return;
    
    setVerifyingAccount(true);
    setAccountName('');
    setVerifiedAccount(false);
    
    try {
      // Call the bank account resolution service through BankService
      const response = await BankService.resolveAccountNumber(accountNumber, selectedBank.code);
      
      console.log('Account verification response:', response);
      
      // Check if the response contains account name data
      if (response && response.status && response.data && response.data.account_name) {
        // Set the account name from the response
        setAccountName(response.data.account_name);
        setVerifiedAccount(true);
        console.log('Account verified successfully:', response.data.account_name);
      } else {
        // Set error state if account verification failed
        setWithdrawalError('Could not verify account. Please check the details and try again.');
        console.log('Account verification failed: No account name returned');
      }
    } catch (err: any) { // Type the error as 'any' to access properties safely
      console.error('Error verifying account:', err);
      
      // Show better error messaging
      if (err.response?.data?.message) {
        setWithdrawalError(err.response.data.message);
        console.error('API error message:', err.response.data.message);
      } else if (err.message) {
        setWithdrawalError(`Account verification failed: ${err.message}`);
      } else {
        setWithdrawalError('Failed to verify account. Please check your account details and try again.');
      }
    } finally {
      setVerifyingAccount(false);
    }
  };

  const handleWithdrawalModalOpen = () => {
    // Reset form state when opening modal
    setWithdrawalAmount('');
    setWithdrawalError(null);
    setWithdrawalSuccess(false);
    setWithdrawalMessage('');
    setSelectedBank(null);
    setAccountNumber('');
    setAccountName('');
    setBankSearchQuery('');
    setVerifiedAccount(false);
    setIsWithdrawalModalOpen(true);
  };

  const handleWithdrawalModalClose = () => {
    if (withdrawalLoading) return; // Prevent closing during processing
    setIsWithdrawalModalOpen(false);
  };

  const handleDetailsModalOpen = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsDetailsModalOpen(true);
  };

  const handleDetailsModalClose = () => {
    setIsDetailsModalOpen(false);
    setSelectedTransaction(null);
  };

  // const refreshTransactionStatus = async (reference: string) => {
  //   if (!reference) return;
    
  //   setRefreshingStatus(reference);
  //   try {
  //     const result = await BalanceService.checkWithdrawalStatus(reference);
  //     console.log('Status update result:', result);
      
  //     // Refresh data to show updated status
  //     await fetchBalance();
      
  //     // Show a success message if needed
  //     if (result.status) {
  //       setError(`Status updated: ${result.message}`);
  //       setTimeout(() => setError(null), 3000);
  //     }
  //   } catch (err) {
  //     console.error('Error refreshing transaction status:', err);
  //     setError('Failed to refresh status. Please try again.');
  //   } finally {
  //     setRefreshingStatus(null);
  //   }
  // };

  const handleWithdrawal = async (e: React.FormEvent) => {
    e.preventDefault();
    setWithdrawalLoading(true);
    setWithdrawalError(null);
    setWithdrawalSuccess(false);
    setWithdrawalMessage('');
  
    try {
      const amount = parseFloat(withdrawalAmount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid amount');
      }
      if (amount > balance) {
        throw new Error('Insufficient balance');
      }
      
      // Validate bank details
      if (!selectedBank) {
        throw new Error('Please select a bank');
      }
      if (!accountNumber || accountNumber.length < 10) {
        throw new Error('Please enter a valid account number');
      }
      if (!verifiedAccount || !accountName) {
        throw new Error('Please verify your account details first');
      }
      
      // Create bank details object matching the BankAccountDetailsDto structure
      const bankDetails = {
        bank_code: selectedBank.code,
        account_number: accountNumber,
        account_name: accountName
      };
      
      // Create withdrawal request matching the WithdrawalRequestDto
      const withdrawalData: WithdrawalRequest = {
        amount,
        accountDetails: JSON.stringify(bankDetails), // This must be a JSON string
        notes: `Withdrawal via bank transfer to ${selectedBank.name}`,
        currency
      };
  
      // For debugging
      console.log('Sending withdrawal request:', withdrawalData);
      console.log('Bank details string:', withdrawalData.accountDetails);
      
      const response = await BalanceService.initiateWithdrawal(withdrawalData);
      setWithdrawalSuccess(true);
      setWithdrawalMessage(response.message || 'Withdrawal request processed successfully');
      
      // Only start polling if the withdrawal status is pending
      const referenceCode = response.reference || (response.data && response.data.reference);
      const initialStatus = response.withdrawalStatus || (response.data && response.data.status);
      
      if (referenceCode) {
        // Only start polling if status is pending or not provided
        if (!initialStatus || initialStatus === 'pending') {
          console.log(`Starting polling for pending withdrawal ${referenceCode}`);
          startPolling(referenceCode);
        } else {
          console.log(`Not polling for withdrawal ${referenceCode} with status ${initialStatus}`);
        }
      }
      
      // Refresh balance after successful withdrawal
      await fetchBalance();
      
      // Auto close modal after success (optional)
      setTimeout(() => {
        if (withdrawalSuccess) {
          setIsWithdrawalModalOpen(false);
        }
      }, 3000);
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process withdrawal';
      setWithdrawalError(errorMessage);
      console.error('Withdrawal error details:', err);
    } finally {
      setWithdrawalLoading(false);
    }
  };
  const formatTransactionType = (type: string) => {
    switch (type) {
      case 'deposit':
        return 'Deposit';
      case 'withdrawal':
        return 'Withdrawal';
      case 'premium_payment':
        return 'Premium Payment';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
    }
  };

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'reversed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPremiumLimits = () => {
    if (!userPremiumInfo.isPremium) {
      return {
        withdrawalsPerDay: 5,
        feePercentage: 10,
        text: 'Basic plan: Up to 5 withdrawals per day with 10% fee'
      };
    }
    
    switch (userPremiumInfo.premiumPlan?.toLowerCase()) {
      case 'ultimate':
        return {
          withdrawalsPerDay: 'Unlimited',
          feePercentage: 0,
          text: 'Ultimate plan: Unlimited withdrawals with 0% fee'
        };
      case 'premium':
        return {
          withdrawalsPerDay: 2,
          feePercentage: 0,
          text: 'Premium plan: Up to 2 withdrawals per day with 0% fee'
        };
      default:
        return {
          withdrawalsPerDay: 5,
          feePercentage: 10,
          text: 'Basic plan: Up to 5 withdrawals per day with 10% fee'
        };
    }
  };

  const exportTransactions = () => {
    if (!transactions.length) return;

    const headers = ['Date', 'Type', 'Amount', 'Currency', 'Status', 'Reference', 'Description'];
    const csvRows = [
      headers.join(','),
      ...transactions.map((tx) =>
        [
          new Date(tx.date).toISOString(),
          formatTransactionType(tx.type),
          tx.amount || 0,
          tx.currency || currency,
          tx.status,
          tx.reference || '',
          `"${tx.message?.replace(/"/g, '""') || ''}"`,
        ].join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter banks based on search query
  const filteredBanks = bankSearchQuery 
    ? banks.filter(bank => 
        bank.name.toLowerCase().includes(bankSearchQuery.toLowerCase())
      )
    : banks;

  const premiumInfo = getPremiumLimits();

  return (
    <AdminLayout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Account Balance
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Manage your wedding fund contributions and withdrawals
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
              <Button
                onClick={exportTransactions}
                variant="outline"
                leftIcon={<Download size={16} />}
                disabled={!transactions.length}
                customClasses="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Export
              </Button>
              <Button
                onClick={handleWithdrawalModalOpen}
                variant="primary"
                leftIcon={<DollarSign size={16} />}
                disabled={loading || balance <= 0}
              >
                Withdraw Funds
              </Button>
            </div>
          </div>

          {error && (
            <Alert
              type="error"
              message={error}
              dismissible
              onDismiss={() => setError(null)}
              className="mt-4"
            />
          )}

          {loading ? (
            <div className="py-12 flex justify-center">
              <Loader size="lg" label="Loading account data..." />
            </div>
          ) : (
            <>
              {/* Balance Cards */}
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {/* Available Balance Card */}
                <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-white bg-opacity-25">
                      <DollarSign className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5">
                      <h3 className="text-lg font-medium text-white">Available Balance</h3>
                      <div className="mt-1">
                        <p className="text-3xl font-semibold text-white">
                          {formatPriceSimple(balance, currency)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <button
                      onClick={handleWithdrawalModalOpen}
                      disabled={balance <= 0}
                      className="text-sm flex items-center text-indigo-100 hover:text-white transition-colors disabled:opacity-50"
                    >
                      <span>Withdraw Funds</span>
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-4 w-4 ml-1" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </Card>

                {/* Total Received Card */}
                <Card className="bg-gradient-to-br from-green-400 to-green-500 text-white">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-white bg-opacity-25">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <div className="ml-5">
                      <h3 className="text-lg font-medium text-white">Total Received</h3>
                      <div className="mt-1">
                        <p className="text-3xl font-semibold text-white">
                          {formatPriceSimple(
                            transactions
                              .filter(tx => tx.type === 'deposit')
                              .reduce((sum, tx) => sum + (tx.amount || 0), 0),
                            currency
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Total Withdrawn Card */}
                <Card className="bg-gradient-to-br from-blue-400 to-blue-500 text-white">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-white bg-opacity-25">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <div className="ml-5">
                      <h3 className="text-lg font-medium text-white">Total Withdrawn</h3>
                      <div className="mt-1">
                        <p className="text-3xl font-semibold text-white">
                          {formatPriceSimple(
                            transactions
                              .filter(tx => tx.type === 'withdrawal')
                              .reduce((sum, tx) => sum + (tx.amount || 0), 0),
                            currency
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

             {/* Withdrawals Card */}
<div className="mt-8">
  <Card title="Withdrawal History">
    {withdrawals.length === 0 ? (
      <div className="py-12 text-center">
        <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">No withdrawals yet</h3>
        <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
          Your withdrawal history will appear here once you make a withdrawal request.
        </p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Date
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Amount
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Bank
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {withdrawals.map((withdrawal) => {
              // Try to extract bank name using properties that exist on Transaction
              let bankName = '';
              
              // Get bank name from bankName property or metadata
              if (withdrawal.bankName) {
                bankName = withdrawal.bankName;
              } else if (withdrawal.metadata?.bankName) {
                bankName = withdrawal.metadata.bankName;
              } 
              // Get bank name from metadata.bank.name if available
              else if (withdrawal.metadata?.bank?.name) {
                bankName = withdrawal.metadata.bank.name;
              }
              // If we have a bankCode in metadata, try to find the bank name
              else if (withdrawal.metadata?.bankCode) {
                const code = withdrawal.metadata.bankCode;
                const bank = banks.find(b => b.code === code);
                if (bank) {
                  bankName = bank.name;
                }
              }
              // Try to get from metadata.bank_details if available
              else if (withdrawal.metadata?.bank_details) {
                const details = withdrawal.metadata.bank_details;
                if (typeof details === 'string') {
                  try {
                    const parsedDetails = JSON.parse(details);
                    bankName = parsedDetails.bank_name || parsedDetails.bankName || '';
                  } catch (e) {
                    // Ignore parsing errors
                    console.log("Error parsing bank_details:", e);
                  }
                } else if (typeof details === 'object' && details !== null) {
                  bankName = details.bank_name || details.bankName || '';
                }
              }
              
              return (
                <tr key={withdrawal.reference || withdrawal._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(withdrawal.date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className="font-medium text-blue-600">
                    {formatPriceSimple(
                      withdrawal.amount || 0,
                      (withdrawal.currency as SupportedCurrency) || currency
                    )}
                  </span>
                  {withdrawal.fee !== undefined && withdrawal.fee > 0 && (
                    <span className="ml-2 text-xs text-gray-500">
                      (Fee: {formatPriceSimple(Number(withdrawal.fee), withdrawal.currency as SupportedCurrency || currency)})
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {withdrawal.accountName || withdrawal.metadata?.accountName || 'N/A'}
                  <div className="text-xs text-gray-400">
                    {withdrawal.accountNumber || withdrawal.metadata?.accountNumber || 'N/A'}
                  </div>
                  {bankName && (
                    <div className="text-xs text-gray-400">
                      {bankName}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        getStatusBadgeClass(withdrawal.status)
                      }`}
                    >
                      {withdrawal.status.charAt(0).toUpperCase() +
                        withdrawal.status.slice(1)}
                    </span>
                    
                    {/* Add status polling indicator */}
                    {pollingActive && 
                     pollingTransactionId === withdrawal.reference && 
                     withdrawal.status === 'pending' && (
                      <span className="ml-2 text-xs text-gray-500 flex items-center">
                        <RefreshCw size={10} className="mr-1 animate-spin" />
                        Checking...
                      </span>
                    )}
                    
                    {/* Show webhook updated indicator for recently updated transactions */}
                    {withdrawal.status === 'completed' && (
                      <span className="ml-2 text-xs text-green-500">
                        <Check size={12} className="inline mr-1" />
                      </span>
                    )}
                    
                    {withdrawal.status === 'failed' && (
                      <span className="ml-2 text-xs text-red-500">
                        <AlertCircle size={12} className="inline mr-1" />
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => refreshTransactionStatus(withdrawal.reference || '')}
                      disabled={
                        refreshingStatus === withdrawal.reference || 
                        withdrawal.status !== 'pending' || 
                        (pollingActive && pollingTransactionId === withdrawal.reference)
                      }
                      className={`text-blue-500 hover:text-blue-700 disabled:opacity-50 ${
                        withdrawal.status !== 'pending' ? 'cursor-not-allowed' : ''
                      }`}
                      title={
                        withdrawal.status === 'pending' 
                          ? "Refresh status" 
                          : withdrawal.status === 'completed'
                            ? "Transaction already completed"
                            : "Transaction failed"
                      }
                    >
                      <RefreshCw 
                        size={16} 
                        className={refreshingStatus === withdrawal.reference ? 'animate-spin' : ''}
                      />
                    </button>
                    <button
                      onClick={() => handleDetailsModalOpen(withdrawal)}
                      className="text-gray-500 hover:text-gray-700"
                      title="View details"
                    >
                      <Info size={16} />
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </Card>
</div>

              {/* ... Rest of the component ... */}
            </>
          )}
        </div>
      </div>

      {/* Withdrawal Modal */}
      <Transition appear show={isWithdrawalModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleWithdrawalModalClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="absolute top-0 right-0 pt-4 pr-4">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500"
                      onClick={handleWithdrawalModalClose}
                      disabled={withdrawalLoading}
                    >
                      <span className="sr-only">Close</span>
                      <X className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Request Withdrawal
                  </Dialog.Title>
                  
                  {/* Success Message */}
                  {withdrawalSuccess ? (
  <div className="mt-4">
    <div className="rounded-md bg-green-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <CheckCircle className="h-5 w-5 text-green-400" aria-hidden="true" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-green-800">Withdrawal Request Successful</h3>
          <div className="mt-2 text-sm text-green-700">
            <p>{withdrawalMessage}</p>
          </div>
          <div className="mt-2 text-sm text-green-700">
            <p>You'll receive an email notification when the transfer is processed.</p>
            
            {/* Add polling indicator */}
            {pollingActive && pollingTransactionId && (
              <div className="mt-2 flex items-center text-xs text-blue-500">
                <RefreshCw size={12} className="animate-spin mr-1" />
                <span>Checking status automatically...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    
    <div className="mt-5 flex justify-end">
      <Button
        type="button"
        variant="primary"
        onClick={handleWithdrawalModalClose}
      >
        Close
      </Button>
    </div>
  </div>
                  ) : (
                    <>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Enter the amount you'd like to withdraw and your bank details.
                        </p>
                        <div className="mt-2 p-2 rounded text-xs">
                          {userPremiumInfo.isPremium ? (
                            <div className="bg-blue-50 p-2 rounded text-blue-700">
                              <p className="font-medium">Premium plan benefits:</p>
                              <p className="mt-1">
                                {userPremiumInfo.premiumPlan?.toLowerCase() === 'ultimate' ? 
                                  '• Unlimited withdrawals with 0% fee' : 
                                  '• Up to 2 withdrawals per day with 0% fee'}
                              </p>
                            </div>
                          ) : (
                            <div className="bg-gray-50 p-2 rounded text-gray-700">
                              <p>Basic plan: Up to 5 withdrawals per day with 10% fee</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Error Message */}
                      {withdrawalError && (
                        <div className="mt-4 rounded-md bg-red-50 p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-red-800">Withdrawal Error</h3>
                              <div className="mt-2 text-sm text-red-700">
                                <p>{withdrawalError}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <form onSubmit={handleWithdrawal} className="mt-4 space-y-4">
                        <div>
                          <label htmlFor="withdrawal-amount" className="block text-sm font-medium text-gray-700">
                            Amount ({currency})
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">{currency}</span>
                            </div>
                            <input
                              type="number"
                              name="withdrawal-amount"
                              id="withdrawal-amount"
                              className="focus:ring-pink-500 focus:border-pink-500 block w-full pl-12 pr-12 sm:text-sm border-gray-300 rounded-md"
                              placeholder="0.00"
                              value={withdrawalAmount}
                              onChange={(e) => setWithdrawalAmount(e.target.value)}
                              required
                              min="100"
                              max={balance.toString()}
                              step="0.01"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                              <button
                                type="button"
                                className="text-pink-500 hover:text-pink-600 text-xs font-medium"
                                onClick={() => setWithdrawalAmount(balance.toString())}
                              >
                                MAX
                              </button>
                            </div>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Minimum withdrawal amount: {formatPriceSimple(100, currency)}
                          </p>
                          {premiumInfo.feePercentage > 0 && parseFloat(withdrawalAmount) > 0 && (
                            <p className="mt-1 text-xs text-gray-500">
                              Fee: {premiumInfo.feePercentage}% ({formatPriceSimple(
                                parseFloat(withdrawalAmount) * premiumInfo.feePercentage / 100, 
                                currency
                              )})
                            </p>
                          )}
                        </div>
                        
                        <div>
                          <label htmlFor="bank-select" className="block text-sm font-medium text-gray-700">
                            Select Your Bank
                          </label>
                          <div className="mt-1 relative">
                            <div className="relative">
                              <input
                                type="text"
                                className="block w-full pr-10 sm:text-sm border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                                placeholder="Search for bank..."
                                value={bankSearchQuery}
                                onChange={(e) => setBankSearchQuery(e.target.value)}
                                disabled={loadingBanks}
                              />
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <Search className="h-5 w-5 text-gray-400" />
                              </div>
                            </div>
                            
                            {loadingBanks ? (
                              <div className="mt-2 flex justify-center">
                                <span className="text-sm text-gray-500">Loading banks...</span>
                              </div>
                            ) : (
                              <>
                                {selectedBank ? (
                                  <div className="mt-2 p-2 border border-gray-200 rounded-md bg-gray-50">
                                    <div className="flex justify-between items-center">
                                      <span className="text-sm font-medium">{selectedBank.name}</span>
                                      <button
                                        type="button"
                                        className="text-xs text-pink-600 hover:text-pink-800"
                                        onClick={() => setSelectedBank(null)}
                                      >
                                        Change
                                      </button>
                                    </div>
                                    <span className="text-xs text-gray-500">Code: {selectedBank.code}</span>
                                  </div>
                                ) : (
                                  <>
                                    {bankSearchQuery && (
                                      <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 text-base max-h-60 overflow-auto focus:outline-none sm:text-sm">
                                        {filteredBanks.length === 0 ? (
                                          <div className="p-4 text-center text-sm text-gray-500">
                                            No banks found matching "{bankSearchQuery}"
                                          </div>
                                        ) : (
                                          <ul className="divide-y divide-gray-200">
                                            {filteredBanks.map((bank) => (
                                              <li
                                                key={bank.id}
                                                className="cursor-pointer hover:bg-gray-100 px-4 py-2 text-sm"
                                                onClick={() => {
                                                  setSelectedBank(bank);
                                                  setBankSearchQuery('');
                                                }}
                                              >
                                                <div className="font-medium">{bank.name}</div>
                                                <div className="text-xs text-gray-500">Code: {bank.code}</div>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                          {banks.length === 0 && !loadingBanks && (
                            <p className="mt-1 text-xs text-red-500">
                              Could not load banks. Please try again later.
                            </p>
                          )}
                        </div>
                        
                        <div>
                          <label htmlFor="account-number" className="block text-sm font-medium text-gray-700">
                            Account Number
                          </label>
                          <input
                            type="text"
                            name="account-number"
                            id="account-number"
                            className="mt-1 focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            placeholder="Enter your account number"
                            value={accountNumber}
                            onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                            required
                            pattern="[0-9]*"
                            minLength={10}
                            maxLength={10}
                            inputMode="numeric"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Enter your 10-digit account number
                          </p>
                        </div>
                        
                        <div>
                          <div className="flex justify-between">
                            <label htmlFor="account-name" className="block text-sm font-medium text-gray-700">
                              Account Holder Name
                            </label>
                            {verifyingAccount && (
                              <span className="text-xs text-blue-500">Verifying...</span>
                            )}
                          </div>
                          <div className="relative mt-1">
                            <input
                              type="text"
                              name="account-name"
                              id="account-name"
                              className={`focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                                verifiedAccount ? 'bg-green-50 border-green-300' : ''
                              }`}
                              placeholder={verifyingAccount ? 'Verifying account...' : 'Account holder name will appear here'}
                              value={accountName}
                              onChange={(e) => setAccountName(e.target.value)}
                              required
                              readOnly={verifiedAccount}
                            />
                            {verifiedAccount && (
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <CheckCircle className="h-5 w-5 text-green-500" />
                              </div>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {verifiedAccount 
                              ? 'Account verified successfully!' 
                              : 'Select a bank and enter your account number to verify'}
                          </p>
                        </div>
                        
                        <div className="mt-5 flex justify-end space-x-3">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleWithdrawalModalClose}
                            disabled={withdrawalLoading}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            variant="primary"
                            disabled={
                              withdrawalLoading || 
                              !withdrawalAmount || 
                              !selectedBank || 
                              !accountNumber || 
                              !accountName ||
                              !verifiedAccount
                            }
                            loading={withdrawalLoading}
                          >
                            {withdrawalLoading ? 'Processing...' : 'Withdraw Funds'}
                          </Button>
                        </div>
                      </form>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Transaction Details Modal */}
      <Transition appear show={isDetailsModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleDetailsModalClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="absolute top-0 right-0 pt-4 pr-4">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500"
                      onClick={handleDetailsModalClose}
                    >
                      <span className="sr-only">Close</span>
                      <X className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Transaction Details
                  </Dialog.Title>
                  
                  {selectedTransaction && (
  <div className="mt-4">
    {/* Status Badge */}
    <div className="flex justify-between items-center">
      <h4 className="text-sm font-medium text-gray-500">
        {formatTransactionType(selectedTransaction.type)}
      </h4>
      <div className="flex items-center">
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            getStatusBadgeClass(selectedTransaction.status)
          }`}
        >
          {selectedTransaction.status.charAt(0).toUpperCase() +
            selectedTransaction.status.slice(1)}
        </span>
        
        {/* Add polling indicator */}
        {pollingActive && 
         pollingTransactionId === selectedTransaction.reference && 
         selectedTransaction.status === 'pending' && (
          <span className="ml-2 text-xs text-blue-500 flex items-center">
            <RefreshCw size={10} className="mr-1 animate-spin" />
            Checking status...
          </span>
        )}
      </div>
    </div>
    
    {/* Amount */}
    <div className="mt-3">
      <h4 className="text-sm font-medium text-gray-500">Amount</h4>
      <p className={`text-xl font-semibold mt-1 ${
        selectedTransaction.type === 'deposit'
          ? 'text-green-600'
          : selectedTransaction.type === 'withdrawal'
          ? 'text-blue-600'
          : 'text-gray-900'
      }`}>
        {selectedTransaction.type === 'deposit' ? '+' : selectedTransaction.type === 'withdrawal' ? '-' : ''}
        {formatPriceSimple(
          selectedTransaction.amount || 0,
          (selectedTransaction.currency as SupportedCurrency) || currency
        )}
      </p>
      
      {/* Fee information */}
      {selectedTransaction.type === 'withdrawal' && 
        selectedTransaction.fee !== undefined && 
        selectedTransaction.fee > 0 && (
        <p className="text-sm text-gray-500 mt-1">
          Fee: {formatPriceSimple(Number(selectedTransaction.fee), selectedTransaction.currency as SupportedCurrency || currency)}
          {' '}({((Number(selectedTransaction.fee) / Number(selectedTransaction.amount || 0)) * 100).toFixed(1)}%)
        </p>
      )}
      
      {/* Amount after fee */}
      {selectedTransaction.type === 'withdrawal' && selectedTransaction.fee !== undefined && (
        <p className="text-sm text-gray-500 mt-1">
          Net amount: {formatPriceSimple(
            Number(selectedTransaction.amount || 0) - Number(selectedTransaction.fee || 0), 
            selectedTransaction.currency as SupportedCurrency || currency
          )}
        </p>
      )}
    </div>
    
    {/* Date */}
    <div className="mt-3">
      <h4 className="text-sm font-medium text-gray-500">Date</h4>
      <p className="text-sm mt-1">{formatDate(selectedTransaction.date)}</p>
    </div>
    
    {/* Reference */}
    <div className="mt-3">
      <h4 className="text-sm font-medium text-gray-500">Reference</h4>
      <p className="text-sm mt-1 break-all">{selectedTransaction.reference || 'N/A'}</p>
    </div>
    
    {/* Transfer Code (for withdrawals) */}
    {selectedTransaction.type === 'withdrawal' && selectedTransaction.transferCode && (
      <div className="mt-3">
        <h4 className="text-sm font-medium text-gray-500">Transfer Code</h4>
        <p className="text-sm mt-1 break-all">{selectedTransaction.transferCode}</p>
      </div>
    )}
    
    {/* Description */}
    <div className="mt-3">
      <h4 className="text-sm font-medium text-gray-500">Description</h4>
      <p className="text-sm mt-1">{selectedTransaction.message || 'No description available'}</p>
    </div>
    
    {/* Bank Details (for withdrawals) */}
    {selectedTransaction.type === 'withdrawal' && (
      <div className="mt-3">
        <h4 className="text-sm font-medium text-gray-500">Bank Details</h4>
        <div className="mt-1 p-2 bg-gray-50 rounded text-sm">
          <p>
            <span className="font-medium">Account:</span>{' '}
            {selectedTransaction.accountName || 
             selectedTransaction.metadata?.accountName || 
             'N/A'}
          </p>
          <p>
            <span className="font-medium">Account Number:</span>{' '}
            {selectedTransaction.accountNumber || 
             selectedTransaction.metadata?.accountNumber || 
             'N/A'}
          </p>
          <p>
            <span className="font-medium">Bank:</span>{' '}
            {selectedTransaction.bankName || 
             selectedTransaction.metadata?.bankName || 
             'N/A'}
          </p>
        </div>
      </div>
    )}
    
    {/* Failure Reason */}
    {selectedTransaction.status === 'failed' && (
      <div className="mt-3">
        <h4 className="text-sm font-medium text-red-500">Failure Reason</h4>
        <p className="text-sm mt-1 text-red-500 bg-red-50 p-2 rounded">
          {selectedTransaction.failureReason || 
           selectedTransaction.metadata?.failureReason || 
           'No reason provided'}
        </p>
      </div>
    )}
    
    {/* Status Indicators */}
    {selectedTransaction.type === 'withdrawal' && (
      <div className="mt-4">
        {selectedTransaction.status === 'pending' ? (
          <>
            <div className="bg-yellow-50 rounded-md p-3 mb-3">
              <p className="text-sm text-yellow-700 flex items-center">
                <AlertCircle className="h-4 w-4 mr-2 text-yellow-500" />
                This transaction is pending and being processed by our payment provider.
              </p>
            </div>
            
            {/* Refresh Status Button */}
            <button
              type="button"
              onClick={() => {
                refreshTransactionStatus(selectedTransaction.reference || '');
              }}
              disabled={
                refreshingStatus === selectedTransaction.reference || 
                (pollingActive && pollingTransactionId === selectedTransaction.reference)
              }
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
            >
              <RefreshCw 
                className={`-ml-1 mr-2 h-4 w-4 ${
                  refreshingStatus === selectedTransaction.reference || 
                  (pollingActive && pollingTransactionId === selectedTransaction.reference) 
                    ? 'animate-spin' : ''}`} 
                aria-hidden="true" 
              />
              {refreshingStatus === selectedTransaction.reference ? 'Refreshing...' : 
               pollingActive && pollingTransactionId === selectedTransaction.reference ? 'Checking...' : 
               'Refresh Status'}
            </button>
            
            {/* Polling indicator */}
            {pollingActive && pollingTransactionId === selectedTransaction.reference && (
              <div className="mt-2 text-xs text-blue-500 flex items-center">
                <span className="animate-pulse mr-1">•</span>
                Status is being checked automatically every few seconds
              </div>
            )}
          </>
        ) : selectedTransaction.status === 'completed' ? (
          <div className="bg-green-50 rounded-md p-3">
            <p className="text-sm text-green-700 flex items-center">
              <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
              This transaction has been completed successfully. The funds have been transferred to your bank account.
            </p>
            {selectedTransaction.metadata?.paystack_response?.transfer_time && (
              <p className="text-xs text-green-600 mt-2">
                Completed on: {formatDate(selectedTransaction.metadata.paystack_response.transfer_time)}
              </p>
            )}
          </div>
        ) : selectedTransaction.status === 'failed' ? (
          <div className="bg-red-50 rounded-md p-3">
            <p className="text-sm text-red-700 flex items-center">
              <AlertCircle className="h-4 w-4 mr-2 text-red-500" />
              This transaction has failed. Please see the failure reason above.
            </p>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-md p-3">
            <p className="text-sm text-gray-700 flex items-center">
              <Info className="h-4 w-4 mr-2 text-gray-500" />
              This transaction is in {selectedTransaction.status} status.
            </p>
          </div>
        )}
      </div>
    )}
  </div>
)}
                  
                  <div className="mt-6 flex justify-end">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleDetailsModalClose}
                    >
                      Close
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </AdminLayout>
  );
};

export default UserBalance;